import { useState, useEffect, useContext } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import bkstApi from "../../api";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { UserContext } from "../../context/UserContext";
import currency from "currency.js";
import { AutoAwesomeOutlined } from "@mui/icons-material";
import { LocalShippingOutlined } from "@mui/icons-material";
import { ForwardToInbox } from "@mui/icons-material";
import { StorefrontOutlined } from "@mui/icons-material";

const CUSTOM_PLAN = {
  midTier: {
    title: "One Simple Price",
    desc: "One flat rate for everything on the BakeStreet Platform",
    pricingTable: process.env.REACT_APP_ONE_PLAN_ID,
    features: [
      {
        title: "Modern POS for Bakeries",
        desc: "Take in-store or phone orders, handle to-go and future pickups and deliveries, take deposits and easily manage order changes—all in one place.",
      },
      {
        title: "Turn Visitors Into Buyers",
        desc: "Get a high-conversion, SEO-optimized website that attracts more customers and turns casual visitors into paying orders, without you having to learn marketing.",
      },
      {
        title: "Sell Your Everyday & Seasonal Menu With Ease",
        desc: "Showcase your products beautifully online. Set rules for standard and premium options, add custom fields, control availability, and ensure customers order exactly what you can fulfill.",
      },
      {
        title: "Bring in Bigger Orders With Catering",
        desc: "Accept large catering orders directly on your site, without back-and-forth emails. Make it easy for customers to say “yes” to you for weddings, events, and corporate gatherings.",
      },
      {
        title: "Fill Your Classes, Automatically",
        desc: "Launch and manage baking or decorating classes right on your website. Customers book and pay online, you focus on teaching.",
      },
      {
        /*
        title: "AI Quotes",
        desc: "AI-powered quoting instantly gives customers accurate pricing based on your rates, handling 90% of requests without your input. That’s hours saved every day and orders confirmed faster. Additional fee applies."
      */
      },
      {
        title: "AI Photo Tags",
        desc: "AI photo tagging organizes hundreds of cake and pastry photos in minutes, so customers can quickly find exactly what they’re dreaming of.",
      },
      {
        title: "Invoices That Close Deals Faster",
        desc: "Smart expiring invoices encourage customers to confirm quickly, cutting down on endless follow-ups.",
      },
      {
        title: "Get Paid Without Chasing",
        desc: "BakeStreet automatically collects balance payments 7 days before pickup or delivery, so no more awkward “your payment is overdue” calls.",
      },
      {
        title: "Earn More in Tips, Without Asking",
        desc: "Preset tip options increase gratuities by 5–7% on average, making it easy for customers to show appreciation.",
      },
      {
        title: "Cover Your Transaction Costs Automatically",
        desc: "Pass along a fixed fee with your quotes so payment processing never cuts into your margins.",
      },
      {
        title: "Protect Yourself From Disputes",
        desc: "Display your cancellation and refund policy clearly at checkout and on order pages, reducing chargebacks and misunderstandings.",
      },
      {
        title: "Access Capital When You Need It",
        desc: "Borrow up to $250,000 with simple terms and automated repayment—so you can grow without the stress of bank paperwork.",
      },
      {
        title: "Keep Your Team in Sync",
        desc: "Give owners, managers, clerks, and kitchen staff their own logins with the exact permissions they need; reducing errors and confusion.",
      },
      {
        title: "Edit Orders Without the Headache",
        desc: "Add items or make changes to confirmed orders without canceling and starting over—refunds and extra charges update automatically.",
      },
      {
        title: "Plan Your Week in Seconds",
        desc: "Generate your weekly baking plan in a single click and track order progress so nothing slips through the cracks.",
      },
      {
        title: "Track Orders From Anywhere",
        desc: "Stay on top of every order, spot anything left unfinished, and catch missed items before your customers do.",
      },
      {
        title: "Know Your Customers Better",
        desc: "With in-app CRM, see loyalty levels, past orders, and quote history at a glance, so every interaction feels personal and informed.",
      },
      {
        title: "Turn Happy Customers Into Marketers",
        desc: "Get more 5 star Google reviews after an order and watch your star rating grow.",
      },
      {
        title: "Never Miss a Customer Question",
        desc: "In-app chat keeps you connected with customers instantly, while your team sees exactly who replied, what was said, and when—no more confusion or running around.",
      },
      {
        title: "Offer Delivery With Confidence",
        desc: "Set powerful rules for in-house or third-party delivery during checkout, and generate a full delivery plan in one click so no more scrambling.",
      },
      {
        title: "Stay on Top of Your Numbers",
        desc: "See sales, payouts, and tip reports anytime. Spot trends early and make informed decisions.",
      },
      /*{
        title: "Manage Multiple Locations With Ease",
        desc: "Track and control orders, staff, and inventory across all your stores from one place. Up to 5 locations included."
      }*/
    ],
  },
};

const GROWTH_PLAN = [
  {
    title: "Grow Custom Orders with AI Quotes",
    desc: "BakeStreet AI sends instant custom quotes using your pricing, saving time, reducing follow-ups, and converting more custom requests into orders.",
  },
  {
    title: "Grow Repeat Business with AI Marketing",
    desc: "When you launch specials or holiday menus, BakeStreet AI sends emails automatically, helping bring customers back while you focus on the kitchen.",
  },
];

export default function MyPlan(props) {
  const { user } = useContext(UserContext);

  const [plan, setPlan] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bkstApi(`/my/planv2`)
      .then((res) => {
        setPlan(res.data.plan);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  }, []);

  const convertPaymentMethodRateToString = (rate) => {
    let str = "";
    if (rate.factor) {
      // remove trailing zeros
      str += (rate.factor * 100).toFixed(2).replace(/\.?0+$/, "") + "%";
    }
    if (+rate.amount > 0) {
      str += ` + ${currency(rate.amount).format()}`;
    }
    if (+rate.floor > 0) {
      str += ` (min ${currency(rate.floor).format()})`;
    }
    return str;
  };

  const getFeeDescription = () => {
    let desc = `Payment Fee | Card Present: ${convertPaymentMethodRateToString(plan.fees.cp)}, Card Not Present: ${convertPaymentMethodRateToString(
      plan.fees.cnp
    )}, Keyed: ${convertPaymentMethodRateToString(plan.fees.moto)}, Card on File: ${convertPaymentMethodRateToString(plan.fees.cf)}. `;
    if (plan.fees.platformFee && +plan.fees.platformFee.floor > 0) {
      desc += ` Convenience Fee (paid by customer): ${convertPaymentMethodRateToString(plan.fees.platformFee)} per online order. `;
    }
    if (plan.successFee) {
      desc += ` Success Fee: ${convertPaymentMethodRateToString(plan.fees.successFee)}`;
    }

    return desc;
  };

  if (loading) {
    return <LinearProgress />;
  } else if (plan) {
    const feeDesc = getFeeDescription();
    return (
      <Container maxWidth="md">
        <Box my={6}>
          <Paper>
            <Box py={3} px={2} style={{ backgroundColor: "#ECECEC" }}>
              <Typography variant="h4">My Plan</Typography>
            </Box>
            <Box p={2}>
              {Object.entries(CUSTOM_PLAN).map(([k, o]) => {
                return (
                  <Box>
                    {o.features.map((val) => {
                      return (
                        <Box my={3}>
                          <Typography variant="h6" gutterBottom>
                            {val.title}
                          </Typography>
                          <Typography variant="body1" color="textSecondary">
                            {val.desc}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Paper>
          {plan?.fees?.model === "cost+" && (
            <Box p={2} textAlign={"center"}>
              <Typography variant="caption" color="textSecondary">
                {feeDesc}
              </Typography>
            </Box>
          )}
          <Box my={4}>
            <Box mb={2}>
              <Typography variant="h4" gutterBottom>
                Add-Ons
              </Typography>
              <Typography variant="body1" color="textSecondary" gutterBottom>
                Optional add-on to supercharge your growth.
              </Typography>
            </Box>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <Paper elevation={0}>
                    <Box p={2}>
                      <AutoAwesomeOutlined sx={{ fontSize: "2.5rem", color: "primary.main" }} />

                      <Typography variant="h6" gutterBottom>
                        Grow Custom Orders with AI Quotes
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        BakeStreet AI sends instant custom quotes using your pricing, saving time,
                        reducing follow-ups, and converting more custom requests into orders. 5% fee
                        on confirmed orders.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Paper elevation={0}>
                    <Box p={2}>
                      <LocalShippingOutlined sx={{ fontSize: "2.5rem", color: "primary.main" }} />

                      <Typography variant="h6" gutterBottom>
                        Grow Orders with Delivery Options
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        Give your customers the convenience they crave. Leverage 3rd party delivery
                        networks, including white-glove services to drive more sales. $2.00 fee on
                        completed orders.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Paper elevation={0}>
                    <Box p={2}>
                      <ForwardToInbox sx={{ fontSize: "2.5rem", color: "primary.main" }} />
                      <Typography variant="h6" gutterBottom>
                        Grow Repeat Business with AI Marketing
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        When you launch specials or holiday menus, BakeStreet AI sends emails
                        automatically, helping bring customers back while you focus on the kitchen.
                        5% fee on confirmed orders.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Paper elevation={0}>
                    <Box p={2}>
                      <StorefrontOutlined sx={{ fontSize: "2.5rem", color: "primary.main" }} />

                      <Typography variant="h6" gutterBottom>
                        Grow Your Footprint with Additional Locations
                      </Typography>
                      <Typography variant="body1" color="textSecondary">
                        Scale your bakery business with ease. Seamlessly manage orders, staff, and
                        production for every new branch you open. Your plan includes 1 location.
                        $100/mo for each additional location.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Container>
    );
  } else {
    return (
      <Container maxWidth="md">
        <Box my={6}>
          <Box align="center" my={4}>
            <Typography variant="h1">One Simple Price</Typography>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              One flat rate for everything on the BakeStreet Platform
            </Typography>
          </Box>
          <Grid container>
            <Grid sm={4} xs={12}></Grid>
            {Object.entries(CUSTOM_PLAN).map(([k, o]) => {
              return (
                <Grid sm={12} xs={12}>
                  <Box py={4} sx={{ border: "1px solid #DDD" }}>
                    <stripe-pricing-table
                      pricing-table-id={o.pricingTable}
                      publishable-key={process.env.REACT_APP_STRIPE_KEY}
                    ></stripe-pricing-table>
                  </Box>
                  <Accordion elevation={0}>
                    <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                      <Typography>See What's Included</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {o.features.map((val) => {
                          return (
                            <Box my={3}>
                              <Typography variant="h4" gutterBottom>
                                {val.title}
                              </Typography>
                              <Typography variant="body1" color="textSecondary">
                                {val.desc}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        Payment Fee | Card Present: 2.9% + $0.30, Card Not Present: 3.5% + $0.15,
                        Keyed: 3.5% + $0.15, Card on File: 3.5% + $0.15. Convenience Fee (paid by
                        customer): 1% (min $2.00) per online order.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Container>
    );
  }
}
